import React, { useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import styles from '../../Components/Form/Inputs/Inputs.module.scss';
import formattingService from '../../Services/Formatting/FormattingService';
import useLanguage from '../../Services/Language/useLanguageHook';

export type UserDataInputCellValue = string | number | Date | null | undefined;
export type UserDataInputCellDate = Date | null | undefined;
export type UserDataInputCellNumber = number | string | null | undefined;

export interface UserDataInputCellHandle {
    isDirty: () => boolean;
    isValid: () => boolean;
}

export interface UserDataInputCellProps {
    value: UserDataInputCellValue;
    required?: boolean;
    isDuplicate?: boolean;
    wrapperClassName?: string;
    dirtyClassName?: string;
    invalidClassName?: string;
    onStateChange?: () => void;
    childIsValid?: boolean;
    errorMessage?: string;
    children: React.ReactNode;
}

const serializeValue = (value: UserDataInputCellValue): string => {
    if (value === null || value === undefined) {
        return '';
    }
    if (typeof value === 'number') {
        return Number.isNaN(value) ? '' : String(value);
    }
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? '' : formattingService.toApiDate(value);
    }
    if (typeof value === 'string') {
        return value.trim();
    }
    return String(value);
};

const isDateValid = (value: UserDataInputCellDate): boolean => {
    if (value instanceof Date) {
        return !Number.isNaN(value.getTime());
    }
    return false;
};

const isNumberValid = (value: UserDataInputCellNumber, required?: boolean): boolean => {
    if (value === null || value === undefined) {
        return !required;
    }
    if (typeof value === 'number') {
        return !Number.isNaN(value) || !required;
    }
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed === '') {
            return !required;
        }
        return !Number.isNaN(Number(trimmed));
    }
    return true;
};

const shouldValidateNumber = (value: UserDataInputCellValue, childIsValid?: boolean): value is UserDataInputCellNumber => {
    if (typeof value === 'number') {
        return true;
    }
    if (value === null && childIsValid === undefined) {
        return true;
    }
    return false;
};

const UserDataInputCell = React.forwardRef<UserDataInputCellHandle, UserDataInputCellProps>((props, reference) => {
    const [translations] = useLanguage();
    const [childIsValidState, setChildIsValidState] = useState<boolean>(true);
    const currentSerializedValue = serializeValue(props.value);

    const initialSerializedValueReference = useRef<string | null>(null);
    if (initialSerializedValueReference.current === null) {
        initialSerializedValueReference.current = currentSerializedValue;
    }

    const baselineSerializedValue = initialSerializedValueReference.current;
    const isNumberField = typeof props.value === 'number' || (props.value === null && props.childIsValid === undefined);
    const childIsValidComputed = shouldValidateNumber(props.value, props.childIsValid) ? isNumberValid(props.value, props.required) : true;
    const childIsValid = props.childIsValid !== undefined ? props.childIsValid : childIsValidState && childIsValidComputed;
    const isValid = childIsValid && !props.isDuplicate;
    const isDirty = currentSerializedValue !== baselineSerializedValue;

    const previousIsDirtyReference = useRef(isDirty);
    const previousIsValidReference = useRef(isValid);
    React.useEffect(() => {
        if (previousIsDirtyReference.current !== isDirty || previousIsValidReference.current !== isValid) {
            previousIsDirtyReference.current = isDirty;
            previousIsValidReference.current = isValid;
            props.onStateChange?.();
        }
    }, [isDirty, isValid, props.onStateChange]);

    const childInputReference = useRef<HTMLDivElement>(null);
    const previousIsDuplicateReference = useRef(props.isDuplicate);
    React.useEffect(() => {
        if (previousIsDuplicateReference.current !== props.isDuplicate && childInputReference.current) {
            const inputElement = childInputReference.current.querySelector('input');
            if (inputElement) {
                inputElement.blur();
                inputElement.focus();
                inputElement.blur();
            }
        }
        previousIsDuplicateReference.current = props.isDuplicate;
    }, [props.isDuplicate]);

    const duplicateDateText = translations.userData.items.errors.duplicateDate;
    const mustBeValidDateText = translations.userData.items.errors.mustBeValidDate;
    const valueRequiredNumberText = translations.userData.items.errors.valueRequiredNumber;

    const validationErrorMessage = useMemo(() => {
        if (props.isDuplicate) {
            return duplicateDateText;
        }
        if (!childIsValid) {
            if (props.errorMessage) {
                return props.errorMessage;
            }
            if (isNumberField) {
                return valueRequiredNumberText;
            }
            return mustBeValidDateText;
        }
        return undefined;
    }, [props.errorMessage, props.isDuplicate, childIsValid, isNumberField, duplicateDateText, valueRequiredNumberText, mustBeValidDateText]);

    const wrapperStateClass = useMemo(() => {
        if (!isValid) {
            return props.invalidClassName || styles.stateful_invalid;
        }
        if (isDirty) {
            return props.dirtyClassName || styles.stateful_dirty;
        }
        return '';
    }, [isValid, isDirty, props.invalidClassName, props.dirtyClassName]);

    useImperativeHandle(
        reference,
        () => ({
            isDirty: () => isDirty,
            isValid: () => isValid,
        }),
        [isDirty, isValid],
    );

    const onValidityChange = useCallback(
        (childValid: boolean) => {
            setChildIsValidState(childValid);
            props.onStateChange?.();
        },
        [props.onStateChange],
    );

    const renderChildren = () =>
        React.Children.map(props.children, (child) => {
            if (!React.isValidElement(child)) {
                return child;
            }

            const childElement = child as React.ReactElement;
            const childProps = childElement.props as {
                handleDateChange?: (date: Date | undefined) => void;
            };
            const expectsDateValue = typeof childProps?.handleDateChange === 'function';

            let normalizedValue = props.value;
            if (expectsDateValue && typeof normalizedValue === 'string' && normalizedValue.trim() !== '') {
                normalizedValue = formattingService.parseDate(normalizedValue, 'YYYY-MM-DD');
            }

            if (normalizedValue instanceof Date && !isDateValid(normalizedValue)) {
                normalizedValue = null;
            } else if (typeof normalizedValue === 'string' && normalizedValue.trim() === '') {
                normalizedValue = null;
            } else if (typeof normalizedValue === 'number' && Number.isNaN(normalizedValue)) {
                normalizedValue = null;
            }

            const validationFunction = props.isDuplicate || !childIsValid ? () => false : null;

            return React.cloneElement(childElement, {
                value: normalizedValue,
                required: props.required,
                onValidityChange,
                validation: validationFunction,
                errorMessage: validationErrorMessage,
            });
        });

    const getDataState = (): string => {
        if (!isValid) {
            return 'invalid';
        }
        if (isDirty) {
            return 'dirty';
        }
        return 'normal';
    };

    return (
        <div
            ref={childInputReference}
            className={`${styles.stateful_wrapper} ${props.wrapperClassName || ''} ${wrapperStateClass}`.trim()}
            data-state={getDataState()}
        >
            {renderChildren()}
        </div>
    );
});

UserDataInputCell.displayName = 'UserDataInputCell';
export default UserDataInputCell;
